/**
 * VIDEO BUBBLES COMPONENT
 * Handles video bubbles interaction, overlay lightbox, and navigation.
 * Supports Shopify-hosted files and YouTube (including Shorts).
 */

if (typeof VideoBubblesComponent !== 'function') {

  class VideoBubblesComponent extends HTMLElement {

    constructor() {
      super();

      this.bubbles = [];
      this.currentIndex = 0;
      this.overlay = null;
      this.overlayVideo = null;
      this.overlayIframe = null;
      this.isOverlayOpen = false;

      this.init();
    }

    init() {
      this.bubbles = Array.from(this.querySelectorAll('.video-bubble'));

      if (this.bubbles.length === 0) return;

      this.setActiveBubble(0);

      this.bubbles.forEach((bubble, index) => {
        bubble.addEventListener('click', (e) => {
          e.preventDefault();
          this.openOverlay(index);
        });

        bubble.addEventListener('mouseenter', () => this.onBubbleHover(bubble, index));
        bubble.addEventListener('mouseleave', () => this.onBubbleLeave(bubble, index));
      });

      this.createOverlay();

      this.bubbles.forEach((bubble, index) => {
        if (this.isYouTubeBubble(bubble)) return;

        const video = bubble.querySelector('.video-bubble__video');
        if (video) {
          video.muted = true;
          video.loop = true;

          if (index === 0) {
            video.preload = 'auto';
            video.load();
            this.primeVideoFrame(video);
          } else {
            video.removeAttribute('autoplay');
            video.preload = 'metadata';
            video.pause();
          }
        }
      });

      setTimeout(() => {
        this.startBubbleAutoplay(0);
      }, 200);

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.onEnterViewport();
          } else {
            this.onExitViewport();
          }
        });
      }, { threshold: 0.2 });

      this.observer.observe(this);
    }

    isYouTubeBubble(bubble) {
      return Boolean(bubble && bubble.dataset.videoType === 'youtube' && bubble.dataset.youtubeId);
    }

    youtubeOverlaySrc(id) {
      return 'https://www.youtube.com/embed/' + id
        + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
    }

    setActiveBubble(index) {
      this.bubbles.forEach((bubble, i) => {
        bubble.classList.toggle('video-bubble--active', i === index);
      });
    }

    primeVideoFrame(video) {
      if (!video) return;

      video.addEventListener('loadeddata', () => {
        video.currentTime = 0.1;
      }, { once: true });

      video.load();
    }

    tryPlayVideo(video, bubble) {
      if (!video) return;

      const attemptPlay = () => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        if (bubble) {
          bubble.setAttribute('data-playing', 'true');
        }
      };

      if (video.readyState >= 2) {
        attemptPlay();
      } else {
        video.addEventListener('canplay', attemptPlay, { once: true });
      }
    }

    onBubbleHover(bubble, index) {
      if (bubble.classList.contains('video-bubble--active')) return;
      if (this.isYouTubeBubble(bubble)) return;

      const video = bubble.querySelector('.video-bubble__video');
      if (video) {
        video.muted = true;
        video.load();
        this.tryPlayVideo(video, bubble);
      }
    }

    onBubbleLeave(bubble, index) {
      if (bubble.classList.contains('video-bubble--active')) return;
      if (this.isYouTubeBubble(bubble)) return;

      const video = bubble.querySelector('.video-bubble__video');
      if (video) {
        video.pause();
        video.currentTime = 0;
        bubble.setAttribute('data-playing', 'false');
      }
    }

    startBubbleAutoplay(index) {
      const bubble = this.bubbles[index];
      if (!bubble || this.isYouTubeBubble(bubble)) return;

      const video = bubble.querySelector('.video-bubble__video');
      if (video) {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.load();
        this.tryPlayVideo(video, bubble);
      }
    }

    stopBubbleAutoplay(index) {
      const bubble = this.bubbles[index];
      if (!bubble || this.isYouTubeBubble(bubble)) return;

      const video = bubble.querySelector('.video-bubble__video');
      if (video) {
        video.pause();
        bubble.setAttribute('data-playing', 'false');
      }
    }

    onEnterViewport() {
      if (!this.isOverlayOpen) {
        this.startBubbleAutoplay(0);
      }
    }

    onExitViewport() {
      this.bubbles.forEach((bubble, index) => {
        this.stopBubbleAutoplay(index);
      });
    }

    createOverlay() {
      if (document.querySelector('.video-bubbles-overlay[data-parent="' + this.dataset.id + '"]')) {
        this.overlay = document.querySelector('.video-bubbles-overlay[data-parent="' + this.dataset.id + '"]');
        this.bindOverlayRefs();
        return;
      }

      const overlayHTML = `
        <div class="video-bubbles-overlay" data-parent="${this.dataset.id}">
          <div class="video-bubbles-overlay__inner">
            <button class="video-bubbles-overlay__close" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            ${this.bubbles.length > 1 ? `
              <button class="video-bubbles-overlay__skip video-bubbles-overlay__skip--first" aria-label="First video">
                <svg viewBox="0 0 24 24"><path d="M19 20L9 12l10-8v16zM7 19V5H5v14h2z"/></svg>
              </button>
              <button class="video-bubbles-overlay__nav video-bubbles-overlay__nav--prev" aria-label="Previous video">
                <svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
              </button>
            ` : ''}

            <div class="video-bubbles-overlay__video-wrapper" data-playing="false" data-muted="false">
              <div class="video-bubbles-overlay__title">
                <span class="video-bubbles-overlay__title-text"></span>
              </div>
              <video class="video-bubbles-overlay__video" playsinline></video>
              <iframe class="video-bubbles-overlay__iframe" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen title=""></iframe>
              <button class="video-bubbles-overlay__mute" aria-label="Toggle mute">
                <svg class="unmuted-icon" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
                <svg class="muted-icon" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
              </button>
              <div class="video-bubbles-overlay__controls">
                <button class="video-bubbles-overlay__play-pause" aria-label="Play/Pause">
                  <svg class="play-icon" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  <svg class="pause-icon" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                </button>
              </div>
            </div>

            ${this.bubbles.length > 1 ? `
              <button class="video-bubbles-overlay__nav video-bubbles-overlay__nav--next" aria-label="Next video">
                <svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
              </button>
              <button class="video-bubbles-overlay__skip video-bubbles-overlay__skip--last" aria-label="Last video">
                <svg viewBox="0 0 24 24"><path d="M5 4l10 8-10 8V4zm12 0v16h2V4h-2z"/></svg>
              </button>
            ` : ''}

            ${this.bubbles.length > 1 ? `
              <div class="video-bubbles-overlay__dots">
                ${this.bubbles.map((_, i) => `<button class="video-bubbles-overlay__dot${i === 0 ? ' is-active' : ''}" data-index="${i}"></button>`).join('')}
              </div>
            ` : ''}
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', overlayHTML);
      this.overlay = document.querySelector('.video-bubbles-overlay[data-parent="' + this.dataset.id + '"]');
      this.bindOverlayRefs();
      this.bindOverlayEvents();
    }

    bindOverlayRefs() {
      if (!this.overlay) return;
      this.overlayVideo = this.overlay.querySelector('.video-bubbles-overlay__video');
      this.overlayIframe = this.overlay.querySelector('.video-bubbles-overlay__iframe');
      this.videoWrapper = this.overlay.querySelector('.video-bubbles-overlay__video-wrapper');
      this.titleText = this.overlay.querySelector('.video-bubbles-overlay__title-text');
    }

    bindOverlayEvents() {
      this.overlay.querySelector('.video-bubbles-overlay__close').addEventListener('click', () => this.closeOverlay());

      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.closeOverlay();
        }
      });

      const prevBtn = this.overlay.querySelector('.video-bubbles-overlay__nav--prev');
      const nextBtn = this.overlay.querySelector('.video-bubbles-overlay__nav--next');
      const firstBtn = this.overlay.querySelector('.video-bubbles-overlay__skip--first');
      const lastBtn = this.overlay.querySelector('.video-bubbles-overlay__skip--last');

      if (prevBtn) prevBtn.addEventListener('click', () => this.navigateVideo(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.navigateVideo(1));
      if (firstBtn) firstBtn.addEventListener('click', () => this.goToVideo(0));
      if (lastBtn) lastBtn.addEventListener('click', () => this.goToVideo(this.bubbles.length - 1));

      const dots = this.overlay.querySelectorAll('.video-bubbles-overlay__dot');
      dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index, 10);
          this.goToVideo(index);
        });
      });

      const playPauseBtn = this.overlay.querySelector('.video-bubbles-overlay__play-pause');
      if (playPauseBtn) {
        playPauseBtn.addEventListener('click', () => this.togglePlayPause());
      }

      const muteBtn = this.overlay.querySelector('.video-bubbles-overlay__mute');
      if (muteBtn) {
        muteBtn.addEventListener('click', () => this.toggleMute());
      }

      if (this.overlayVideo) {
        this.overlayVideo.addEventListener('play', () => {
          this.videoWrapper.setAttribute('data-playing', 'true');
        });

        this.overlayVideo.addEventListener('pause', () => {
          this.videoWrapper.setAttribute('data-playing', 'false');
        });

        this.overlayVideo.addEventListener('ended', () => {
          if (this.currentIndex < this.bubbles.length - 1) {
            this.navigateVideo(1);
          }
        });
      }

      document.addEventListener('keydown', (e) => {
        if (!this.isOverlayOpen) return;

        switch (e.key) {
          case 'Escape':
            this.closeOverlay();
            break;
          case 'ArrowLeft':
            this.navigateVideo(-1);
            break;
          case 'ArrowRight':
            this.navigateVideo(1);
            break;
          case ' ':
            e.preventDefault();
            this.togglePlayPause();
            break;
          case 'm':
            this.toggleMute();
            break;
        }
      });

      this.onResize = () => {
        if (this.isOverlayOpen) {
          this.setOverlayVideoSize();
        }
      };
      window.addEventListener('resize', this.onResize);
    }

    openOverlay(index) {
      this.currentIndex = index;
      this.isOverlayOpen = true;

      this.bubbles.forEach((_, i) => this.stopBubbleAutoplay(i));

      this.loadVideoAtIndex(index);

      this.overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';

      this.updateNavigationState();
    }

    resetOverlayMedia() {
      if (this.overlayVideo) {
        this.overlayVideo.pause();
        this.overlayVideo.currentTime = 0;
        this.overlayVideo.src = '';
        this.overlayVideo.onloadedmetadata = null;
      }
      if (this.overlayIframe) {
        this.overlayIframe.src = '';
        this.overlayIframe.removeAttribute('title');
      }
      if (this.videoWrapper) {
        this.videoWrapper.classList.remove('is-loading', 'is-youtube');
        this.videoWrapper.style.width = '';
        this.videoWrapper.style.height = '';
      }
    }

    closeOverlay() {
      this.isOverlayOpen = false;

      this.resetOverlayMedia();

      this.overlay.classList.remove('is-active');
      document.body.style.overflow = '';

      this.startBubbleAutoplay(0);
    }

    loadVideoAtIndex(index) {
      const bubble = this.bubbles[index];
      if (!bubble) return;

      const videoTitle = bubble.dataset.videoTitle || '';

      this.videoWrapper.classList.add('is-loading');
      this.videoWrapper.style.width = '';
      this.videoWrapper.style.height = '';

      if (this.titleText) {
        this.titleText.textContent = videoTitle;
      }

      if (this.isYouTubeBubble(bubble)) {
        if (this.overlayVideo) {
          this.overlayVideo.pause();
          this.overlayVideo.src = '';
          this.overlayVideo.onloadedmetadata = null;
        }

        this.videoWrapper.classList.add('is-youtube');
        this.setOverlayYoutubeSize(bubble.dataset.youtubeShort === 'true');

        if (this.overlayIframe) {
          this.overlayIframe.title = videoTitle;
          this.overlayIframe.src = this.youtubeOverlaySrc(bubble.dataset.youtubeId);
        }

        this.videoWrapper.classList.remove('is-loading');
        this.videoWrapper.setAttribute('data-playing', 'true');
        this.updateDots(index);
        return;
      }

      this.videoWrapper.classList.remove('is-youtube');
      if (this.overlayIframe) {
        this.overlayIframe.src = '';
      }

      const videoSrc = bubble.dataset.videoSrc;
      this.overlayVideo.src = videoSrc;
      this.overlayVideo.muted = false;
      this.videoWrapper.setAttribute('data-muted', 'false');
      this.overlayVideo.load();

      this.overlayVideo.onloadedmetadata = () => {
        this.setOverlayVideoSize();
        this.videoWrapper.classList.remove('is-loading');
        this.overlayVideo.play().catch(() => {
          this.videoWrapper.setAttribute('data-playing', 'false');
        });
      };

      if (this.overlayVideo.readyState >= 1) {
        this.setOverlayVideoSize();
        this.videoWrapper.classList.remove('is-loading');
      }

      this.updateDots(index);
    }

    navigateVideo(direction) {
      const newIndex = this.currentIndex + direction;
      if (newIndex >= 0 && newIndex < this.bubbles.length) {
        this.goToVideo(newIndex);
      }
    }

    goToVideo(index) {
      if (index < 0 || index >= this.bubbles.length) return;

      this.currentIndex = index;
      this.loadVideoAtIndex(index);
      this.updateNavigationState();
    }

    updateNavigationState() {
      const prevBtn = this.overlay.querySelector('.video-bubbles-overlay__nav--prev');
      const nextBtn = this.overlay.querySelector('.video-bubbles-overlay__nav--next');
      const firstBtn = this.overlay.querySelector('.video-bubbles-overlay__skip--first');
      const lastBtn = this.overlay.querySelector('.video-bubbles-overlay__skip--last');

      if (prevBtn) prevBtn.disabled = this.currentIndex === 0;
      if (nextBtn) nextBtn.disabled = this.currentIndex === this.bubbles.length - 1;
      if (firstBtn) firstBtn.disabled = this.currentIndex === 0;
      if (lastBtn) lastBtn.disabled = this.currentIndex === this.bubbles.length - 1;
    }

    updateDots(index) {
      const dots = this.overlay.querySelectorAll('.video-bubbles-overlay__dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    setOverlayYoutubeSize(isShort) {
      if (!this.videoWrapper) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxWidth = vw < 750 ? vw * 0.7 : vw * 0.5;
      const maxHeight = vw < 750 ? vh * 0.7 : vh * 0.8;
      const ratio = isShort ? 9 / 16 : 16 / 9;

      let width = maxWidth;
      let height = width / ratio;

      if (height > maxHeight) {
        height = maxHeight;
        width = height * ratio;
      }

      this.videoWrapper.style.width = `${Math.round(width)}px`;
      this.videoWrapper.style.height = `${Math.round(height)}px`;
    }

    setOverlayVideoSize() {
      if (!this.overlayVideo || !this.videoWrapper) return;

      const currentBubble = this.bubbles[this.currentIndex];
      if (this.isYouTubeBubble(currentBubble)) {
        this.setOverlayYoutubeSize(currentBubble.dataset.youtubeShort === 'true');
        return;
      }

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxWidth = vw < 750 ? vw * 0.7 : vw * 0.5;
      const maxHeight = vw < 750 ? vh * 0.7 : vh * 0.8;

      const videoWidth = this.overlayVideo.videoWidth || maxWidth;
      const videoHeight = this.overlayVideo.videoHeight || maxHeight;

      let width = videoWidth;
      let height = videoHeight;

      const widthRatio = maxWidth / width;
      const heightRatio = maxHeight / height;
      const scale = Math.min(1, widthRatio, heightRatio);

      width = Math.round(width * scale);
      height = Math.round(height * scale);

      this.videoWrapper.style.width = `${width}px`;
      this.videoWrapper.style.height = `${height}px`;
    }

    togglePlayPause() {
      if (!this.overlayVideo || this.videoWrapper.classList.contains('is-youtube')) return;

      if (this.overlayVideo.paused) {
        this.overlayVideo.play();
      } else {
        this.overlayVideo.pause();
      }
    }

    toggleMute() {
      if (!this.overlayVideo || this.videoWrapper.classList.contains('is-youtube')) return;

      this.overlayVideo.muted = !this.overlayVideo.muted;
      this.videoWrapper.setAttribute('data-muted', this.overlayVideo.muted ? 'true' : 'false');
    }

  }

  if (typeof customElements.get('video-bubbles-component') === 'undefined') {
    customElements.define('video-bubbles-component', VideoBubblesComponent);
  }

}
